import { useEffect, useReducer, useState } from 'react';

export const FOCUS_TOOLS_STORAGE_KEY = 'dopatize:focus-tools:v1';
export const MAX_REWARDS = 50;
export const MAX_REWARD_LENGTH = 160;
const MINUTE = 60 * 1000;

export const formatCountdown = (remainingMs) => {
  const seconds = Math.ceil(remainingMs / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

const validMinutes = (value) => Number.isInteger(value) && value >= 1 && value <= 180;

const idleTimer = (minutes) => ({
  phase: 'focus', status: 'idle', durationMs: minutes * MINUTE,
  remainingMs: minutes * MINUTE, endsAt: null, reward: null, acknowledged: true, completedAt: null,
});

export const initialFocusTools = () => ({
  version: 1, focusMinutes: 25, breakMinutes: 5, rewards: [], soundEnabled: true,
  timer: idleTimer(25),
});

export const remainingMilliseconds = (timer, now) => (
  timer.status === 'running'
    ? Math.max(0, Math.min(timer.durationMs, timer.endsAt - now))
    : timer.remainingMs
);

const finishIfDue = (state, now, random) => {
  if (state.timer.status !== 'running' || state.timer.endsAt > now) return state;
  const reward = state.timer.phase === 'focus' && state.rewards.length
    ? state.rewards[Math.floor(random * state.rewards.length)]
    : null;
  return {
    ...state,
    timer: { ...state.timer, status: 'finished', remainingMs: 0, endsAt: null, reward, acknowledged: false, completedAt: now },
  };
};

export const focusToolsReducer = (state, action) => {
  switch (action.type) {
    case 'tick':
      return finishIfDue(state, action.now, action.random);
    case 'start': {
      if (!['idle', 'finished'].includes(state.timer.status)) return state;
      const phase = action.phase === 'break' ? 'break' : 'focus';
      const durationMs = (phase === 'focus' ? state.focusMinutes : state.breakMinutes) * MINUTE;
      return {
        ...state,
        timer: {
          phase, status: 'running', durationMs, remainingMs: durationMs,
          endsAt: action.now + durationMs,
          reward: phase === 'break' ? state.timer.reward : null,
          acknowledged: true, completedAt: null,
        },
      };
    }
    case 'pause': {
      const current = finishIfDue(state, action.now, action.random);
      if (current.timer.status !== 'running') return current;
      return {
        ...current,
        timer: {
          ...current.timer, status: 'paused', endsAt: null,
          remainingMs: remainingMilliseconds(current.timer, action.now),
        },
      };
    }
    case 'resume':
      if (state.timer.status !== 'paused') return state;
      return { ...state, timer: { ...state.timer, status: 'running', endsAt: action.now + state.timer.remainingMs } };
    case 'reset':
      return { ...state, timer: idleTimer(state.focusMinutes) };
    case 'acknowledge':
      return { ...state, timer: { ...state.timer, acknowledged: true } };
    case 'sound':
      return { ...state, soundEnabled: Boolean(action.enabled) };
    case 'minutes': {
      if (!['focusMinutes', 'breakMinutes'].includes(action.field)
        || !validMinutes(action.value) || ['running', 'paused'].includes(state.timer.status)) return state;
      const next = { ...state, [action.field]: action.value };
      if (state.timer.status === 'idle') next.timer = idleTimer(next.focusMinutes);
      return next;
    }
    case 'addReward': {
      const reward = typeof action.reward === 'string' ? action.reward.trim() : '';
      if (!reward || reward.length > MAX_REWARD_LENGTH || state.rewards.length >= MAX_REWARDS
        || state.rewards.some((item) => item.toLowerCase() === reward.toLowerCase())) return state;
      return { ...state, rewards: [...state.rewards, reward] };
    }
    case 'removeReward':
      return { ...state, rewards: state.rewards.filter((reward) => reward !== action.reward) };
    case 'importRewards': {
      if (!Array.isArray(action.rewards)) return state;
      return action.rewards.reduce((next, reward) => focusToolsReducer(next, { type: 'addReward', reward }), state);
    }
    default:
      return state;
  }
};

const loadFocusTools = () => {
  const defaults = initialFocusTools();
  try {
    const saved = JSON.parse(window.localStorage.getItem(FOCUS_TOOLS_STORAGE_KEY));
    if (saved?.version !== 1) return defaults;
    const focusMinutes = validMinutes(saved.focusMinutes) ? saved.focusMinutes : defaults.focusMinutes;
    const breakMinutes = validMinutes(saved.breakMinutes) ? saved.breakMinutes : defaults.breakMinutes;
    const rewards = Array.isArray(saved.rewards) ? saved.rewards.reduce((items, value) => {
      if (typeof value !== 'string') return items;
      const reward = value.trim();
      if (reward && reward.length <= MAX_REWARD_LENGTH && items.length < MAX_REWARDS
        && !items.some((item) => item.toLowerCase() === reward.toLowerCase())) items.push(reward);
      return items;
    }, []) : [];
    let timer = idleTimer(focusMinutes);
    const candidate = saved.timer;
    if (candidate && ['focus', 'break'].includes(candidate.phase)
      && ['running', 'paused', 'finished'].includes(candidate.status)
      && Number.isInteger(candidate.durationMs) && candidate.durationMs >= MINUTE && candidate.durationMs <= 180 * MINUTE
      && Number.isFinite(candidate.remainingMs) && candidate.remainingMs >= 0 && candidate.remainingMs <= candidate.durationMs
      && (candidate.status !== 'paused' || candidate.remainingMs > 0)
      && (candidate.status !== 'running' || (Number.isFinite(candidate.endsAt)
        && candidate.endsAt > 0 && candidate.endsAt <= Date.now() + candidate.durationMs))) {
      timer = {
        phase: candidate.phase, status: candidate.status, durationMs: candidate.durationMs,
        remainingMs: candidate.status === 'finished' ? 0 : candidate.remainingMs,
        endsAt: candidate.status === 'running' ? candidate.endsAt : null,
        reward: typeof candidate.reward === 'string' && candidate.reward.length <= MAX_REWARD_LENGTH ? candidate.reward : null,
        acknowledged: candidate.acknowledged === true,
        completedAt: Number.isFinite(candidate.completedAt) ? candidate.completedAt : 0,
      };
    }
    return { ...defaults, focusMinutes, breakMinutes, rewards, timer,
      soundEnabled: typeof saved.soundEnabled === 'boolean' ? saved.soundEnabled : true };
  } catch (_error) {
    return defaults;
  }
};

export default function usePomodoro() {
  const [state, dispatch] = useReducer(focusToolsReducer, undefined, loadFocusTools);
  const [now, setNow] = useState(Date.now);
  const [storageUnavailable, setStorageUnavailable] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(FOCUS_TOOLS_STORAGE_KEY, JSON.stringify(state));
      setStorageUnavailable(false);
    } catch (_error) {
      setStorageUnavailable(true);
    }
  }, [state]);

  useEffect(() => {
    if (state.timer.status !== 'running') return undefined;
    // A deadline survives sleeping tabs and refreshes; interval counts do not.
    const update = () => {
      const time = Date.now();
      setNow(time);
      dispatch({ type: 'tick', now: time, random: Math.random() });
    };
    update();
    const interval = window.setInterval(update, 250);
    const deadline = window.setTimeout(update, Math.max(0, state.timer.endsAt - Date.now()));
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(deadline);
      window.removeEventListener('focus', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [state.timer.status, state.timer.endsAt]);

  const act = (type, values = {}) => {
    const time = Date.now();
    setNow(time);
    dispatch({ type, now: time, random: Math.random(), ...values });
  };

  return { state, act, storageUnavailable, remainingMs: remainingMilliseconds(state.timer, now) };
}
